import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SectionCard, Row, InfoNote, KpiCard } from "@/components/common/Cards";
import { fmtNum, fmtPct, fmtRp, downloadCsv, MONTHS_ID } from "@/lib/format";

export default function IspResultsPanel({ data, result: r }) {
  const exportProjection = () => {
    const rows = [["Bulan", "Pelanggan", "Pendapatan", "Biaya", "Laba kotor", "Arus kas", "Kumulatif"]];
    r.projection.rows.forEach((x) => rows.push([x.month, x.subs, Math.round(x.revenue), Math.round(x.cost), Math.round(x.profit), Math.round(x.cash), Math.round(x.cumulative)]));
    downloadCsv(`isp-projection-${data.region.name.replace(/\s+/g, "-")}.csv`, rows);
  };
  return (
    <div className="space-y-5">
      <div className="grid lg:grid-cols-2 gap-5">
        <SectionCard title="Ringkasan biaya & pendapatan" eyebrow="Per bulan" testid="isp-summary-card">
          <Row label="CAPEX total" value={fmtRp(r.capexTotal)} />
          <Row label="Depresiasi CAPEX / bulan" value={fmtRp(r.capexMonthly)} muted />
          <Row label="OPEX / bulan" value={fmtRp(r.opexMonthly)} muted />
          <Row label="Biaya bandwidth / bulan" value={fmtRp(r.bandwidthCost)} muted />
          <Row label="CPE amortisasi (per pelanggan)" value={fmtRp(r.cpePerSub)} muted />
          <Row label="Total biaya / bulan" value={fmtRp(r.totalCost)} strong />
          <Row label="Pendapatan / bulan" value={fmtRp(r.revenue)} strong />
          <Row label="Laba kotor / bulan" value={fmtRp(r.grossProfit)} strong testid="isp-gross-profit" />
          <Row label="Gross margin" value={fmtPct(r.marginPct)} />
        </SectionCard>
        <SectionCard title="Indikator utama" eyebrow="KPI" testid="isp-kpi-card">
          <Row label="Pelanggan efektif" value={fmtNum(r.totalSubs)} />
          {r.takeRate !== null && <Row label="Take-rate dari homes passed" value={fmtPct(r.takeRate, 1)} muted />}
          <Row label="Kapasitas yang dibutuhkan" value={`${fmtNum(Math.round(r.requiredMbps))} Mbps`} muted />
          <Row label="Kapasitas dibeli (termasuk minimum)" value={`${fmtNum(Math.round(r.billedMbps))} Mbps`} muted />
          <Row label="COGS per Mbps" value={fmtRp(r.cogsPerMbps)} muted />
          <Row label="Biaya per pelanggan" value={fmtRp(r.costPerSub)} muted />
          <Row label="Break-even pelanggan" value={r.breakEvenSubs ? `${fmtNum(r.breakEvenSubs)} subs` : "–"} strong />
          <Row label="EBITDA / bulan (tanpa depresiasi)" value={fmtRp(r.ebitda)} muted />
          <Row label="Payback period CAPEX" value={r.paybackMonths ? `${fmtNum(r.paybackMonths, 1)} bulan` : "–"} strong testid="isp-payback-months" />
        </SectionCard>
      </div>

      <SectionCard title="Komposisi biaya" testid="isp-composition-card">
        <div className="space-y-2">
          {r.composition.map((c) => {
            const total = r.composition.reduce((a, x) => a + x.value, 0) || 1;
            const w = (c.value / total) * 100;
            return (
              <div key={c.name} className="flex items-center gap-3 text-xs" data-testid={`isp-composition-${c.name}`}>
                <span className="w-52 text-foreground">{c.name}</span>
                <div className="flex-1 h-2 rounded-full bg-[hsl(220_10%_13%)] overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${w}%` }} />
                </div>
                <span className="num w-24 text-right">{fmtRp(c.value)}</span>
                <span className="num w-14 text-right text-muted-foreground">{fmtPct(w, 1)}</span>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard
        title="Proyeksi bulanan"
        description={`Horizon ${data.projection.horizonMonths} bulan · ramp ${data.projection.rampMonths} bulan`}
        actions={
          <Button variant="secondary" size="sm" onClick={exportProjection} className="border border-border" data-testid="isp-projection-csv-button">
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
        }
        bodyClassName="p-0"
        testid="isp-projection-card"
      >
        <div className="overflow-x-auto scroll-gold">
          <Table className="dense-table">
            <TableHeader>
              <TableRow className="bg-secondary">
                <TableHead className="text-left">Bulan</TableHead>
                <TableHead className="text-right">Pelanggan</TableHead>
                <TableHead className="text-right">Pendapatan</TableHead>
                <TableHead className="text-right">Biaya</TableHead>
                <TableHead className="text-right">Laba bersih</TableHead>
                <TableHead className="text-right">Arus kas</TableHead>
                <TableHead className="text-right">Kumulatif</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.projection.rows.map((x) => {
                const isBreakeven = r.projection.breakEvenMonth === x.month;
                const isPayback = r.projection.paybackMonth === x.month;
                return (
                  <TableRow key={x.month} className={isBreakeven || isPayback ? "bg-[hsl(43_30%_7%)]" : ""}>
                    <TableCell className="font-medium">
                      Bulan {x.month}
                      {isBreakeven && <span className="ml-2 text-[hsl(160_55%_60%)] text-[10px] uppercase">Break-even</span>}
                      {isPayback && <span className="ml-2 text-primary text-[10px] uppercase">Payback</span>}
                    </TableCell>
                    <TableCell className="text-right num">{fmtNum(x.subs)}</TableCell>
                    <TableCell className="text-right num">{fmtRp(x.revenue)}</TableCell>
                    <TableCell className="text-right num text-muted-foreground">{fmtRp(x.cost)}</TableCell>
                    <TableCell className={`text-right num ${x.profit < 0 ? "text-[hsl(0_72%_64%)]" : "text-[hsl(160_55%_60%)]"}`}>{fmtRp(x.profit)}</TableCell>
                    <TableCell className="text-right num text-muted-foreground">{fmtRp(x.cash)}</TableCell>
                    <TableCell className={`text-right num ${x.cumulative < 0 ? "text-[hsl(0_72%_64%)]" : "text-primary"}`}>{fmtRp(x.cumulative)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <InfoNote>
        Proyeksi pakai rata-rata harga & bandwidth tertimbang berdasarkan mix (wilayah baru) atau komposisi pelanggan existing. Payback dihitung dari arus kas kumulatif.
      </InfoNote>
    </div>
  );
}
